/** Shared building blocks for the "Duelo de Equipos" module. */
import React, { useEffect, useRef, useState } from "react";
import { X as XIcon } from "lucide-react";

export const TEAM_COLOR = { A: "#1D4E89", B: "#E3000F" };

export const wsUrl = (code) => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const proto = base.startsWith("https") ? "wss" : "ws";
  return `${proto}://${base.replace(/^https?:\/\//, "")}/api/team-duel/ws/${code}`;
};

export function StrikeDots({ count, max }) {
  return (
    <div className="flex gap-1 justify-center" data-testid="duel-strikes">
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
        <span
          key={n}
          className={`inline-flex items-center justify-center w-5 h-5 border-2 border-black rounded ${n <= count ? "text-white" : "text-neutral-300 bg-white"}`}
          style={n <= count ? { background: "#E3000F" } : undefined}
        >
          <XIcon className="w-3.5 h-3.5" strokeWidth={4} />
        </span>
      ))}
    </div>
  );
}

export function PlayerCard({ player, isAnswerer, maxStrikes, lang }) {
  const elim = player.eliminated;
  return (
    <div
      data-testid={`duel-player-${player.seller_id}`}
      className={`brutal-sm p-2 text-center relative ${isAnswerer && !elim ? "ring-4 ring-canales-yellow" : ""}`}
      style={{ background: elim ? "#9ca3af" : "#fff", opacity: elim ? 0.9 : 1 }}
    >
      <div className="font-display font-black text-sm truncate text-black" title={player.name}>{player.name}</div>
      <div className="font-display font-black text-2xl leading-none" style={{ color: elim ? "#374151" : "#E3000F" }}>
        {player.points}
      </div>
      <div className="mt-1"><StrikeDots count={player.strikes} max={maxStrikes} /></div>
      {elim && (
        <div className="mt-1 text-[9px] font-display font-black uppercase tracking-widest text-white bg-neutral-700 rounded px-1 py-0.5 inline-block">
          {lang === "es" ? "Fuera del juego" : "Out"}
        </div>
      )}
      {isAnswerer && !elim && (
        <div className="absolute -top-2 -right-2 bg-canales-yellow border-2 border-black rounded-full px-1.5 text-[9px] font-display font-black">
          {lang === "es" ? "TURNO" : "TURN"}
        </div>
      )}
    </div>
  );
}

export function Options({ question, lang, selectable, reveal, onPick }) {
  if (!question) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="duel-options">
      {question.options.map((o) => {
        const text = lang === "en" && o.en ? o.en : o.es;
        let cls = "bg-white text-black";
        if (reveal) {
          if (o.id === reveal.correct) cls = "bg-green-600 text-white";
          else if (o.id === reveal.selected) cls = "bg-canales-red text-white";
        }
        return (
          <button
            key={o.id}
            disabled={!selectable || !!reveal}
            onClick={() => onPick && onPick(o.id)}
            data-testid={`duel-option-${o.id}`}
            className={`brutal-sm px-3 py-3 text-left font-display font-bold flex items-start gap-2 ${cls} ${selectable && !reveal ? "hover:-translate-y-0.5 transition-transform" : "cursor-default"}`}
          >
            <span className="font-black text-canales-blue shrink-0">{o.id}</span>
            <span className="text-sm">{text}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Client-side smooth countdown derived from the server's `time_remaining`. */
export function useCountdown(state) {
  const [sec, setSec] = useState(0);
  const endRef = useRef(0);
  useEffect(() => {
    const active = state && ["playing", "steal", "sudden_death"].includes(state.status);
    if (active && (state.time_remaining ?? 0) >= 0) {
      endRef.current = Date.now() + (state.time_remaining || 0) * 1000;
      setSec(state.time_remaining || 0);
    } else {
      endRef.current = 0;
      setSec(state?.status === "paused" ? (state.time_remaining || 0) : 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.status, state?.q_index, state?.phase, state?.time_remaining]);
  useEffect(() => {
    const id = setInterval(() => {
      if (!endRef.current) return;
      setSec(Math.max(0, Math.round((endRef.current - Date.now()) / 1000)));
    }, 250);
    return () => clearInterval(id);
  }, []);
  return sec;
}

export function DuelShell({ children }) {
  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "#0d1a3f" }}>
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.25) 1.5px, transparent 1.5px)", backgroundSize: "22px 22px" }}
      />
      <div className="absolute -left-40 top-24 w-[720px] h-[90px] opacity-80 pointer-events-none" style={{ background: "#FFD100", transform: "rotate(-20deg)" }} />
      <div className="absolute -right-40 bottom-40 w-[520px] h-[70px] opacity-70 pointer-events-none" style={{ background: "#FFD100", transform: "rotate(-20deg)" }} />
      <div className="relative z-10">{children}</div>
    </div>
  );
}
